import {getValueForBreakpoint} from '../utils/breakpoint-util';
import pubsub from '../utils/pubsub';
import screenUtil from '../utils/screen';
import {
	getValueFromThreshold,
	isValidThresholdValue
} from '../utils/threshold-util';

const DEFAULT_THRESHOLD = 300,
	DEFAULT_DELAY = 30000,
	REFRESH_MODES = {
		'VIEW': 'view',
		'TIME': 'time'
	};

let slotsToRefresh,
	waitForInView,
	waitForOutOfView,
	waitForFirstTimeInView,
	waitForActiveWindow,
	mainViewThreshold,
	mainRefreshDelay;

function init(settings) {
	waitForInView = [];
	waitForOutOfView = [];
	waitForFirstTimeInView = [];
	waitForActiveWindow = [];
	slotsToRefresh = [];

	mainViewThreshold = settings.refreshThreshold ?? DEFAULT_THRESHOLD;
	mainRefreshDelay = settings.refreshDelay ?? DEFAULT_DELAY;

	pubsub.subscribe('slot.rendered', _onSlotRendered);
	pubsub.subscribe('scroll', _onScroll);
	document.addEventListener('visibilitychange', _onVisibilityChanged);
}

function _getDelay(slot) {
	const delay = getValueForBreakpoint(slot.refreshDelay) ?? mainRefreshDelay,
		parsed = parseInt(delay, 10);

	if (typeof parsed !== 'number' || isNaN(parsed)) {
		return DEFAULT_DELAY;
	}

	return parsed;
}

function _onSlotRendered(slot) {
	if (_shouldRefresh(slot)) {
		if (screenUtil.isInView(slot.node, 0)) {
			_handleRefresh(slot);
		} else {
			waitForFirstTimeInView.push(slot);
		}
	}
}

function _shouldRefresh(slot) {
	if (!getValueForBreakpoint(slot.refreshable)) {
		return false;
	}

	if (slot.refreshMediationOnly) {
		return slot.isMediated && !slot.isMediationEmpty;
	}

	return true;
}

function _handleRefresh(slot) {
	// It's already being refreshed
	if (slotsToRefresh.some((st) => st.slot === slot)) {
		return;
	}

	const slotState = {
		slot,
		'timer': null
	};

	if (_isViewModeRefresh(slot)) {
		waitForOutOfView.push(slot);
	}

	if (_isTimeModeRefresh(slot)) {
		slotState.timer = setTimeout(() => {
			_doRefresh(slot, REFRESH_MODES.TIME);
		}, _getDelay(slot));
	}

	slotsToRefresh.push(slotState);
}

function _doRefresh(slot, refreshMode) {
	const idx = slotsToRefresh.findIndex((st) => st.slot === slot);

	if (idx === -1) {
		return;
	}

	if (document.visibilityState !== 'visible') {
		waitForActiveWindow.push({
			slot,
			refreshMode
		});

		return;
	}

	clearTimeout(slotsToRefresh[idx].timer);
	slotsToRefresh[idx].slot.refresh(refreshMode);
	slotsToRefresh.splice(idx, 1);
}

function _isViewModeRefresh(slot) {
	return !slot.refreshModes || slot.refreshModes.indexOf(REFRESH_MODES.VIEW) !== -1;
}

function _isTimeModeRefresh(slot) {
	return slot.refreshModes && slot.refreshModes.indexOf(REFRESH_MODES.TIME) !== -1;
}

function _onScroll() {
	waitForOutOfView = waitForOutOfView.filter((slot) => {
		if (!screenUtil.isInView(slot.node, _getSlotThreshold(slot))) {
			waitForInView.push(slot);

			return false;
		}

		return true;
	});

	waitForInView = waitForInView.filter((slot) => {
		if (screenUtil.isInView(slot.node, _getSlotThreshold(slot))) {
			_doRefresh(slot, REFRESH_MODES.VIEW);

			return false;
		}

		return true;
	});

	waitForFirstTimeInView = waitForFirstTimeInView.filter((slot) => {
		if (screenUtil.isInView(slot.node, 0)) {
			_handleRefresh(slot);

			return false;
		}

		return true;
	});
}

function _getSlotThreshold(slot) {
	const threshold = slot.refreshThreshold ?? mainViewThreshold,
		parsed = getValueFromThreshold(threshold);

	if (!isValidThresholdValue(parsed)) {
		return DEFAULT_THRESHOLD;
	}

	return parsed;
}

function _onVisibilityChanged() {
	if (document.visibilityState === 'visible') {
		waitForActiveWindow.forEach(({slot, refreshMode}) => _doRefresh(slot, refreshMode));
	}
}

export default {
	init,
	REFRESH_MODES
};
